/**
 * Sens de lecture d'un indicateur, et couleur qui en découle.
 *
 * ÉTAT ACTUEL : AUCUNE COULEUR. Voir `COLORATION_PAR_LE_SENS` ci-dessous.
 *
 * Historique, en deux temps :
 *
 * 1. La couleur suivait la DIRECTION, vert quand ça monte et rouge quand ça descend. C'est
 *    un contresens pour une banque centrale : une baisse du taux directeur est un
 *    assouplissement décidé par la Banque, pas une contre-performance. La réunion du 10 août
 *    2026 a demandé de corriger cela.
 * 2. Une contre-proposition a été soumise : garder la couleur mais lui faire dire le SENS du
 *    mouvement pour cet indicateur-là. C'est la machinerie de ce module.
 * 3. La réunion du 11 août 2026 a réexaminé la question et tranché pour une présentation
 *    NEUTRE, « afin de ne pas associer systématiquement une hausse ou une baisse à une
 *    appréciation positive ou négative ». La couleur disparaît donc entièrement.
 *
 * La flèche, elle, n'a jamais bougé : elle continue de dire la direction. Le lecteur garde
 * l'information de mouvement, sans qu'aucun jugement ne s'y attache.
 */

/** Ce qui compte comme une bonne nouvelle pour un indicateur donné. */
export type IndicatorPolarity = "hausse-favorable" | "baisse-favorable" | "neutre";

/** Le jugement porté sur un mouvement précis, une fois le sens connu. */
export type TrendTone = "favorable" | "defavorable" | "neutre";

/** La direction telle que la portent les données (bandeau comme cartes). */
export type TrendDirection = "up" | "down" | "flat";

/**
 * Minuscules, sans accents, ponctuation réduite à des espaces. La barre oblique est
 * conservée : c'est elle qui signe une paire de devises (« USD/CDF »).
 */
function normaliser(libelle: string | undefined): string {
	return (libelle ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9/]+/g, " ")
		.trim();
}

/**
 * Instruments de politique monétaire : la Banque les FIXE, elle ne les subit pas. Testés en
 * premier pour que « coefficient de réserve obligatoire » ne soit pas pris pour une réserve
 * de change au motif qu'il en porte le mot.
 */
const INSTRUMENTS =
	/(taux directeur|policy rate|key rate|taux d interet|interest rate|interbancaire|interbank|adjudication|refinancement|refinancing|coefficient|bon bcc|bons bcc|treasury bill)/;

/**
 * LE SEUL ARBITRAGE OUVERT DE CE MODULE, isolé ici pour qu'il se change en un mot.
 *
 * Le cours se lit en francs par devise étrangère : une hausse est une dépréciation du franc.
 * Deux lectures défendables, et le choix n'est pas technique :
 *
 * - `"baisse-favorable"` (valeur retenue) : le point de vue de la Banque, dont la stabilité
 *   des prix est le mandat. Une dépréciation alimente l'inflation importée et pèse sur le
 *   ménage payé en francs qui achète des biens importés.
 * - `"neutre"` : le sens dépend du lecteur. Dans une économie largement dollarisée, celui
 *   dont le salaire, le loyer ou les transferts de la diaspora sont en devises y gagne.
 *
 * CE CHOIX EST SANS PRÉCÉDENT CONNU, et c'est délibéré. Relevé du 11/08/2026 : les cours de
 * change sont publiés SANS couleur par la BCEAO, Bank Al-Maghrib, la BCE, la Réserve
 * fédérale (H.10), la Banque centrale du Kenya, la Banque de réserve d'Afrique du Sud et la
 * Banque centrale du Nigeria. Les afficheurs de marché, eux, colorent, mais dans l'autre
 * sens : vert quand le chiffre monte, donc vert quand le franc se déprécie. C'est ce que
 * faisait l'ancien site de la BCC, et c'est précisément ce que la réunion du 10 août a
 * demandé de corriger. La coloration par le sens est donc un parti pris propre à la BCC,
 * assumé comme tel, et non l'application d'une convention.
 *
 * Un seul mot à changer ici : le bandeau, la vue complète et la carte de l'accueil suivent.
 */
export const POLARITE_DU_CHANGE: IndicatorPolarity = "baisse-favorable";

/** Testé avant les autres familles pour qu'aucune ne le rattrape par un mot voisin. */
const CHANGE = /(cours de change|taux de change|exchange rate|marche des changes|\b[a-z]{3}\/cdf\b)/;

/** Le renchérissement des prix se lit à la baisse : c'est le mandat même de la Banque. */
const BAISSE_FAVORABLE = /(inflation|prix a la consommation|consumer price)/;

/** Ce que la Banque accumule se lit à la hausse. */
const HAUSSE_FAVORABLE = /(reserves|avoirs|reserve de change|foreign exchange reserve)/;

/** Le sens d'un indicateur, déduit de son libellé. Inconnu = neutre. */
export function polariteIndicateur(libelle: string | undefined): IndicatorPolarity {
	const texte = normaliser(libelle);
	if (!texte) return "neutre";
	if (INSTRUMENTS.test(texte)) return "neutre";
	if (CHANGE.test(texte)) return POLARITE_DU_CHANGE;
	if (BAISSE_FAVORABLE.test(texte)) return "baisse-favorable";
	if (HAUSSE_FAVORABLE.test(texte)) return "hausse-favorable";
	return "neutre";
}

/**
 * L'INTERRUPTEUR DE LA COULEUR, et le seul.
 *
 * `false` : présentation neutre, décision de la réunion technique du 11 août 2026. Toutes
 * les variations sont grises, quel que soit l'indicateur et quelle que soit la direction.
 *
 * `true` : la couleur revient et dit le sens du mouvement pour chaque indicateur, selon les
 * familles reconnues plus haut. La machinerie est conservée intacte, et couverte par ses
 * tests, précisément parce que cet arbitrage a déjà changé deux fois : le rétablir est
 * l'affaire d'un mot, pas d'un chantier.
 */
export const COLORATION_PAR_LE_SENS = false;

/** Le jugement porté sur un mouvement : favorable, défavorable, ou aucun. */
export function tonTendance(
	direction: TrendDirection | undefined,
	polarite: IndicatorPolarity,
): TrendTone {
	if (!COLORATION_PAR_LE_SENS) return "neutre";
	if (!direction || direction === "flat" || polarite === "neutre") return "neutre";
	const attendue: TrendDirection = polarite === "hausse-favorable" ? "up" : "down";
	return direction === attendue ? "favorable" : "defavorable";
}

/** Raccourci du cas courant : on n'a que le libellé et la direction. */
export function tonIndicateur(
	libelle: string | undefined,
	direction: TrendDirection | undefined,
): TrendTone {
	return tonTendance(direction, polariteIndicateur(libelle));
}

/** Bandeau et vue complète : texte coloré sur fond clair. */
export const TON_TEXTE: Record<TrendTone, string> = {
	favorable: "text-[#15B07A]",
	defavorable: "text-[#BE3A3A]",
	neutre: "text-[#5D6E8C]",
};

/**
 * Cartes de l'accueil : la flèche. Le gris est ici un cran plus soutenu que celui du trait
 * plat (slate-300), sans quoi une flèche neutre mais bien réelle passerait pour un décor.
 */
export const TON_FLECHE: Record<TrendTone, string> = {
	favorable: "text-[#15B07A]",
	defavorable: "text-[#D93B3B]",
	neutre: "text-slate-400",
};

/** Cartes de l'accueil : la pastille teintée qui groupe la flèche et la variation. */
export const TON_PASTILLE: Record<TrendTone, string> = {
	favorable: "bg-[#E7F8F1] text-[#15805B]",
	defavorable: "bg-[#FDECEC] text-[#B7282B]",
	neutre: "bg-slate-100 text-slate-600",
};
